import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Tooltip,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PaletteIcon from '@mui/icons-material/Palette';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ImageLibrarySelectionModal from '../inventory/ImageLibrarySelectionModal';
import { EINVOICE_API, ACCOUNTING_API, handleApiError } from '../../../config/api';
import { einvoiceApi, accountingApi } from '../../../config/apiClient';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../common/guide/GuideButton';
import { getCompanyInfo, updateCompanyInfo } from '../../../services/migration/companyDataService';
import { getBranchFirmInfo, updateBranchFirmInfo } from '../../../services/migration/branchDataService';

/** Şablon listesi satırı (list yanıtı — içerik hariç) */
interface XsltTemplateItem {
  id: number;
  name: string;
  documentType: 'EFATURA' | 'EARSIV' | 'HEPSI';
  branchId?: string | null;
  isDefault: boolean;
  contentLength: number;
  hasLogo?: boolean;
  hasStamp?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface XsltTemplateModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * E-Fatura/E-Arşiv tasarım şablonu (XSLT) yönetimi.
 *
 * - Şablonlar şirket bazlı saklanır; istenirse şubeye (franchise) özel tanımlanır.
 * - "Varsayılan" işaretli şablon gönderimde otomatik olarak UBL'e gömülür;
 *   hiçbiri varsayılan değilse entegratörün hesap şablonu kullanılır.
 * - Firma/şube logosu şablondaki __FIRMA_LOGO__ alanına gönderim sırasında
 *   otomatik enjekte edilir (şube logosu → şirket logosu).
 */
const XsltTemplateModal: React.FC<XsltTemplateModalProps> = ({ open, onClose }) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const { branches, userDetails } = useAuth();
  const companyId = userDetails?.companyId || '';

  const [items, setItems] = useState<XsltTemplateItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Düzenleme formu (null → liste görünümü).
  // logoUrl/stampUrl: bu tasarıma ÖZEL görseller ('' → şube/şirket görseline düşülür).
  const [editing, setEditing] = useState<{
    id?: number;
    name: string;
    documentType: string;
    branchId: string;
    isDefault: boolean;
    content: string;
    logoUrl: string;
    stampUrl: string;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [systemDefaultLoading, setSystemDefaultLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Görsel önizleme: son faturalardan biri seçilir, şablon backend'de HTML'e çevrilir.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplateSel, setPreviewTemplateSel] = useState<string>('system');
  const [previewTemplateName, setPreviewTemplateName] = useState<string>('');
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [sampleInvoices, setSampleInvoices] = useState<any[]>([]);
  const [previewInvoiceId, setPreviewInvoiceId] = useState<string>('');

  // Logo + Kaşe/İmza yönetimi: 'company' → şirket kaydı (Companies.LogoUrl/StampImageUrl),
  // şube id → şube/franchise kaydı (Branches.FirmLogoUrl/FirmStampUrl).
  type ImageSlot = 'logo' | 'stamp';
  const [logoScope, setLogoScope] = useState<string>('company');
  const [currentImages, setCurrentImages] = useState<{ logo: string | null; stamp: string | null }>({ logo: null, stamp: null });
  const [logoLoading, setLogoLoading] = useState(false);
  const [logoSaving, setLogoSaving] = useState(false);
  const [logoMessage, setLogoMessage] = useState<string | null>(null);
  // Kütüphane modalı: hangi slot ve hangi hedef için açık.
  // target 'scope' → şirket/şube kaydına yazılır, 'template' → düzenlenen şablona yazılır.
  const [librarySlot, setLibrarySlot] = useState<{ slot: ImageSlot; target: 'scope' | 'template' } | null>(null);
  const logoFileInputRef = useRef<HTMLInputElement>(null);
  const stampFileInputRef = useRef<HTMLInputElement>(null);
  const tplLogoFileInputRef = useRef<HTMLInputElement>(null);
  const tplStampFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setEditing(null);
      setError(null);
      setSuccess(null);
      loadTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await einvoiceApi.post(EINVOICE_API.XSLT_TEMPLATES_LIST, {});
      setItems(response.data?.items || []);
    } catch (err) {
      console.error('[XSLT] Şablonlar yüklenemedi:', err);
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // Sistem varsayılanını kopyalayıp yeni şablon olarak düzenlemeye açar.
  const handleCopySystemDefault = async () => {
    setSystemDefaultLoading(true);
    setError(null);
    try {
      const response = await einvoiceApi.post(EINVOICE_API.XSLT_TEMPLATES_SYSTEM_DEFAULT, {});
      if (!response.data?.success) throw new Error(response.data?.error || 'Şablon alınamadı');
      setEditing({
        name: tt('Sistem Varsayılanı Kopyası', 'Copy of System Default'),
        documentType: 'HEPSI',
        branchId: '',
        isDefault: items.length === 0,
        content: response.data.content,
        logoUrl: '',
        stampUrl: '',
      });
    } catch (err) {
      console.error('[XSLT] Sistem varsayılanı alınamadı:', err);
      setError(handleApiError(err));
    } finally {
      setSystemDefaultLoading(false);
    }
  };

  // .xslt/.xsl dosyasından yeni şablon yükler.
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setEditing({
        name: file.name.replace(/\.(xslt?|xml)$/i, ''),
        documentType: 'HEPSI',
        branchId: '',
        isDefault: items.length === 0,
        content: String(reader.result || ''),
        logoUrl: '',
        stampUrl: '',
      });
    };
    reader.readAsText(file, 'UTF-8');
    e.target.value = '';
  };

  // Mevcut şablonu düzenlemeye açar (içerik get ucundan gelir).
  const handleEdit = async (item: XsltTemplateItem) => {
    setError(null);
    try {
      const response = await einvoiceApi.post(EINVOICE_API.XSLT_TEMPLATES_GET, { id: item.id });
      const t = response.data?.template;
      if (!t) throw new Error('Şablon bulunamadı');
      setEditing({
        id: t.id,
        name: t.name,
        documentType: t.documentType || 'HEPSI',
        branchId: t.branchId || '',
        isDefault: !!t.isDefault,
        content: t.content,
        logoUrl: t.logoUrl || '',
        stampUrl: t.stampUrl || '',
      });
    } catch (err) {
      console.error('[XSLT] Şablon açılamadı:', err);
      setError(handleApiError(err));
    }
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.name.trim()) {
      setError(tt('Şablon adı zorunludur.', 'Template name is required.'));
      return;
    }
    if (!editing.content.trim()) {
      setError(tt('Şablon içeriği boş olamaz.', 'Template content cannot be empty.'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await einvoiceApi.post(EINVOICE_API.XSLT_TEMPLATES_SAVE, {
        id: editing.id ?? null,
        name: editing.name.trim(),
        documentType: editing.documentType,
        branchId: editing.branchId || null,
        content: editing.content,
        isDefault: editing.isDefault,
        logoUrl: editing.logoUrl || null,
        stampUrl: editing.stampUrl || null,
      });
      setSuccess(tt('Şablon kaydedildi.', 'Template saved.'));
      setEditing(null);
      await loadTemplates();
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      console.error('[XSLT] Şablon kaydedilemedi:', err);
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: XsltTemplateItem) => {
    if (!window.confirm(tt(
      `"${item.name}" şablonu silinsin mi?`,
      `Delete template "${item.name}"?`
    ))) return;
    setError(null);
    try {
      await einvoiceApi.post(EINVOICE_API.XSLT_TEMPLATES_DELETE, { id: item.id });
      await loadTemplates();
    } catch (err) {
      console.error('[XSLT] Şablon silinemedi:', err);
      setError(handleApiError(err));
    }
  };

  // Varsayılanı hızlı değiştir: içerik dahil kaydet gerekir → get + save.
  const handleToggleDefault = async (item: XsltTemplateItem) => {
    setError(null);
    try {
      const response = await einvoiceApi.post(EINVOICE_API.XSLT_TEMPLATES_GET, { id: item.id });
      const t = response.data?.template;
      if (!t) throw new Error('Şablon bulunamadı');
      await einvoiceApi.post(EINVOICE_API.XSLT_TEMPLATES_SAVE, {
        id: t.id,
        name: t.name,
        documentType: t.documentType,
        branchId: t.branchId || null,
        content: t.content,
        isDefault: !item.isDefault,
        // Şablonun kendi görselleri korunur (save tam durumu yazar).
        logoUrl: t.logoUrl || null,
        stampUrl: t.stampUrl || null,
      });
      await loadTemplates();
    } catch (err) {
      console.error('[XSLT] Varsayılan değiştirilemedi:', err);
      setError(handleApiError(err));
    }
  };

  // Seçili kapsamın (şirket/şube) mevcut logo + kaşe görsellerini yükler.
  const loadCurrentImages = async (scope: string) => {
    setLogoLoading(true);
    setLogoMessage(null);
    setCurrentImages({ logo: null, stamp: null });
    try {
      if (scope === 'company') {
        const info = await getCompanyInfo(companyId);
        setCurrentImages({ logo: info?.logoUrl || null, stamp: info?.stampImageUrl || null });
      } else {
        const firm = await getBranchFirmInfo(companyId, scope);
        setCurrentImages({ logo: firm?.firmLogoUrl || null, stamp: firm?.firmStampUrl || null });
      }
    } catch (err) {
      console.warn('[XSLT] Logo/kaşe yüklenemedi:', err);
    } finally {
      setLogoLoading(false);
    }
  };

  useEffect(() => {
    if (open && !editing) loadCurrentImages(logoScope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, logoScope]);

  // Görseli küçültüp (maks 400x220) PNG data URI'ye çevirir — SQL'de küçük kalsın.
  const resizeImageToDataUri = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Dosya okunamadı'));
      reader.onload = () => {
        const img = new Image();
        img.onerror = () => reject(new Error('Görsel açılamadı'));
        img.onload = () => {
          const maxW = 400, maxH = 220;
          const scale = Math.min(1, maxW / img.width, maxH / img.height);
          const w = Math.max(1, Math.round(img.width * scale));
          const h = Math.max(1, Math.round(img.height * scale));
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (!ctx) { reject(new Error('Canvas desteklenmiyor')); return; }
          ctx.drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/png'));
        };
        img.src = String(reader.result);
      };
      reader.readAsDataURL(file);
    });

  // Görseli kaydeder ('' → temizler). Şirket → Companies.LogoUrl/StampImageUrl,
  // şube → Branches.FirmLogoUrl/FirmStampUrl.
  const saveImage = async (slot: ImageSlot, value: string) => {
    setLogoSaving(true);
    setLogoMessage(null);
    try {
      if (logoScope === 'company') {
        await updateCompanyInfo(companyId, slot === 'logo'
          ? { logoUrl: value }
          : { stampImageUrl: value });
      } else {
        // update-firm-details ucu TÜM Firm* alanlarını istekten yazar; diğer firma
        // bilgileri silinmesin diye mevcut değerler okunup görselle birlikte gönderilir.
        const existing = (await getBranchFirmInfo(companyId, logoScope)) || {};
        await updateBranchFirmInfo(companyId, logoScope, {
          ...existing,
          ...(slot === 'logo' ? { firmLogoUrl: value } : { firmStampUrl: value }),
        });
      }
      setCurrentImages(prev => ({ ...prev, [slot]: value || null }));
      const label = slot === 'logo' ? tt('Logo', 'Logo') : tt('Kaşe/imza', 'Stamp/signature');
      setLogoMessage(value
        ? tt(`${label} kaydedildi. Bundan sonraki gönderimlerde faturaya eklenir.`, `${label} saved. It will be added to invoices on future sends.`)
        : tt(`${label} kaldırıldı.`, `${label} removed.`));
    } catch (err) {
      console.error('[XSLT] Görsel kaydedilemedi:', err);
      setError(handleApiError(err));
    } finally {
      setLogoSaving(false);
    }
  };

  const handleImageFile = async (slot: ImageSlot, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError(tt('Lütfen bir görsel dosyası seçin (PNG/JPG).', 'Please choose an image file (PNG/JPG).'));
      return;
    }
    try {
      const dataUri = await resizeImageToDataUri(file);
      await saveImage(slot, dataUri);
    } catch (err: any) {
      setError(err?.message || tt('Görsel işlenemedi.', 'Image could not be processed.'));
    }
  };

  // Şablona özel görsel: düzenleme formundaki state'e yazılır, Kaydet ile şablonla birlikte saklanır.
  const setTemplateImage = (slot: ImageSlot, value: string) => {
    setEditing(prev => prev && { ...prev, [slot === 'logo' ? 'logoUrl' : 'stampUrl']: value });
  };

  const handleTemplateImageFile = async (slot: ImageSlot, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError(tt('Lütfen bir görsel dosyası seçin (PNG/JPG).', 'Please choose an image file (PNG/JPG).'));
      return;
    }
    try {
      const dataUri = await resizeImageToDataUri(file);
      setTemplateImage(slot, dataUri);
    } catch (err: any) {
      setError(err?.message || tt('Görsel işlenemedi.', 'Image could not be processed.'));
    }
  };

  // Önizleme için son faturaları getirir (en yeni üstte).
  const loadSampleInvoices = async (): Promise<any[]> => {
    if (sampleInvoices.length > 0) return sampleInvoices;
    try {
      const url = ACCOUNTING_API.INVOICE_BY_COMPANY({
        page: 1,
        pageSize: 20,
        sortColumn: 'invoiceDate',
        sortDirection: 'desc',
      });
      const resp = await accountingApi.get(url);
      const list: any[] = resp.data?.data?.items || resp.data?.items || resp.data?.data || [];
      const arr = Array.isArray(list) ? list : [];
      setSampleInvoices(arr);
      return arr;
    } catch (err) {
      console.warn('[XSLT] Örnek faturalar yüklenemedi:', err);
      return [];
    }
  };

  // Önizleme penceresini açar; ilk açılışta en yeni fatura otomatik seçilir.
  const handleOpenPreview = async (templateSel: string, templateName: string) => {
    setPreviewTemplateSel(templateSel);
    setPreviewTemplateName(templateName);
    setPreviewHtml('');
    setPreviewError(null);
    setPreviewOpen(true);
    const invoices = await loadSampleInvoices();
    const invId = previewInvoiceId || invoices[0]?.id || '';
    if (!invId) {
      setPreviewError(tt(
        'Önizleme için kayıtlı bir fatura gerekiyor; henüz fatura bulunamadı.',
        'A saved invoice is required for preview; no invoice found yet.'
      ));
      return;
    }
    setPreviewInvoiceId(invId);
    await runPreview(templateSel, invId);
  };

  // Şablonu seçili faturayla HTML'e dönüştürür.
  const runPreview = async (templateSel: string, invoiceId: string) => {
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewHtml('');
    try {
      const resp = await einvoiceApi.post(EINVOICE_API.XSLT_TEMPLATES_PREVIEW, {
        invoiceId,
        templateId: templateSel,
      });
      if (resp.data?.success) {
        setPreviewHtml(resp.data.html || '');
      } else {
        setPreviewError(resp.data?.error || tt('Önizleme oluşturulamadı.', 'Preview could not be generated.'));
      }
    } catch (err) {
      console.error('[XSLT] Önizleme hatası:', err);
      setPreviewError(handleApiError(err));
    } finally {
      setPreviewLoading(false);
    }
  };

  const branchName = (branchId?: string | null) => {
    if (!branchId) return tt('Şirket Geneli', 'Company-wide');
    const found = branches?.find((b: any) => b.id === branchId);
    return found?.name || branchId;
  };

  const docTypeLabel = (dt: string) =>
    dt === 'EFATURA' ? tt('E-Fatura', 'E-Invoice')
      : dt === 'EARSIV' ? tt('E-Arşiv', 'E-Archive')
      : tt('Hepsi', 'All');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PaletteIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          {tt('Fatura Tasarım Şablonları (XSLT)', 'Invoice Design Templates (XSLT)')}
          <Typography variant="caption" color="text.secondary" display="block">
            {tt(
              'Varsayılan şablon gönderimde faturaya gömülür; şablon yoksa entegratörün tasarımı kullanılır.',
              'The default template is embedded into the invoice on send; without one, the integrator design is used.'
            )}
          </Typography>
        </Box>
        <GuideButton guideKey="documents.xsltTemplates" size="small" />
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {!editing && (
          <>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={systemDefaultLoading ? <CircularProgress size={16} /> : <ContentCopyIcon />}
                onClick={handleCopySystemDefault}
                disabled={systemDefaultLoading}
              >
                {tt('Sistem Varsayılanından Oluştur', 'Create from System Default')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current?.click()}
              >
                {tt('Dosyadan Yükle (.xslt)', 'Upload from File (.xslt)')}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<VisibilityIcon />}
                onClick={() => handleOpenPreview('system', tt('Sistem Varsayılanı', 'System Default'))}
              >
                {tt('Sistem Varsayılanını Önizle', 'Preview System Default')}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xslt,.xsl,.xml"
                hidden
                onChange={handleFileUpload}
              />
            </Box>

            {/* Fatura görselleri: logo + kaşe/imza — şirket geneli veya şubeye özel */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: 'rgba(25, 118, 210, 0.04)' }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                {tt('Fatura Görselleri (Logo + Kaşe/İmza)', 'Invoice Images (Logo + Stamp/Signature)')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {tt(
                  'Logo faturanın üst kısmında, kaşe/imza görseli faturanın alt kısmında (sağda) gösterilir. Gönderim sırasında şablona otomatik eklenirler. Şubeye (franchise) özel görsel tanımlarsanız o şubenin faturalarında şirketinki yerine o kullanılır. Öncelik sırası: tasarıma özel görsel → şube görseli → şirket görseli. Tasarıma özel görseli şablonu düzenlerken tanımlayabilirsiniz.',
                  'The logo appears at the top of the invoice; the stamp/signature image at the bottom right. Both are injected automatically on send. A branch (franchise) specific image overrides the company one for that branch\'s invoices. Priority: design-specific image → branch image → company image. Design-specific images are set while editing a template.'
                )}
              </Typography>

              <TextField
                select
                size="small"
                label={tt('Kapsam', 'Scope')}
                value={logoScope}
                onChange={(e) => setLogoScope(e.target.value)}
                sx={{ minWidth: 220, mb: 1.5 }}
              >
                <MenuItem value="company">{tt('Şirket Geneli', 'Company-wide')}</MenuItem>
                {(branches || []).filter((b: any) => b.id !== 'null').map((b: any) => (
                  <MenuItem key={b.id} value={b.id}>{b.name} — {tt('Şubeye Özel', 'Branch-specific')}</MenuItem>
                ))}
              </TextField>

              {(['logo', 'stamp'] as const).map((slot) => {
                const current = currentImages[slot];
                const label = slot === 'logo' ? tt('Logo', 'Logo') : tt('Kaşe / İmza', 'Stamp / Signature');
                const fileRef = slot === 'logo' ? logoFileInputRef : stampFileInputRef;
                return (
                  <Box key={slot} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: slot === 'logo' ? 1.5 : 0 }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 90 }}>{label}</Typography>
                    <Box
                      sx={{
                        width: 140,
                        height: 70,
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'white',
                        overflow: 'hidden',
                      }}
                    >
                      {logoLoading ? (
                        <CircularProgress size={18} />
                      ) : current ? (
                        <Box component="img" src={current} alt={label} sx={{ maxWidth: '100%', maxHeight: '100%' }} />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {tt('Görsel yok', 'No image')}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={logoSaving ? <CircularProgress size={14} /> : <PhotoLibraryIcon />}
                        disabled={logoSaving || logoLoading}
                        onClick={() => setLibrarySlot({ slot, target: 'scope' })}
                      >
                        {tt('Kütüphaneden Seç', 'Select from Library')}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={logoSaving ? <CircularProgress size={14} /> : <UploadFileIcon />}
                        disabled={logoSaving || logoLoading}
                        onClick={() => fileRef.current?.click()}
                      >
                        {tt('Bilgisayardan Yükle', 'Upload from Computer')}
                      </Button>
                      {current && (
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={<DeleteIcon />}
                          disabled={logoSaving || logoLoading}
                          onClick={() => saveImage(slot, '')}
                        >
                          {tt('Kaldır', 'Remove')}
                        </Button>
                      )}
                      <input
                        ref={fileRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        hidden
                        onChange={(e) => handleImageFile(slot, e)}
                      />
                    </Box>
                  </Box>
                );
              })}

              {logoMessage && (
                <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                  {logoMessage}
                </Typography>
              )}
            </Paper>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : items.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {tt(
                    'Henüz özel şablon yok. Şablon tanımlanmazsa faturalar entegratörün (EDM/Pavo) hesap tasarımıyla oluşur.',
                    'No custom templates yet. Without a template, invoices are rendered with the integrator (EDM/Pavo) account design.'
                  )}
                </Typography>
              </Paper>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {items.map((item) => (
                  <Paper key={item.id} variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title={item.isDefault
                      ? tt('Varsayılan — gönderimde otomatik kullanılır', 'Default — used automatically on send')
                      : tt('Varsayılan yap', 'Make default')}>
                      <IconButton size="small" color={item.isDefault ? 'warning' : 'default'} onClick={() => handleToggleDefault(item)}>
                        {item.isDefault ? <StarIcon /> : <StarBorderIcon />}
                      </IconButton>
                    </Tooltip>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight="bold" noWrap>{item.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(item.contentLength / 1024).toFixed(0)} KB
                        {item.updatedAt ? ` • ${new Date(item.updatedAt).toLocaleDateString(isTr ? 'tr-TR' : 'en-US')}` : ''}
                      </Typography>
                    </Box>
                    <Chip size="small" label={docTypeLabel(item.documentType)} color={item.documentType === 'HEPSI' ? 'default' : 'primary'} variant="outlined" />
                    <Chip size="small" label={branchName(item.branchId)} color={item.branchId ? 'secondary' : 'default'} variant="outlined" />
                    {item.hasLogo && (
                      <Tooltip title={tt('Bu tasarıma özel logo tanımlı', 'Design-specific logo defined')}>
                        <Chip size="small" label={tt('Özel Logo', 'Own Logo')} color="success" variant="outlined" />
                      </Tooltip>
                    )}
                    {item.hasStamp && (
                      <Tooltip title={tt('Bu tasarıma özel kaşe/imza tanımlı', 'Design-specific stamp/signature defined')}>
                        <Chip size="small" label={tt('Özel Kaşe', 'Own Stamp')} color="success" variant="outlined" />
                      </Tooltip>
                    )}
                    <Tooltip title={tt('Örnek faturayla önizle', 'Preview with a sample invoice')}>
                      <IconButton size="small" color="secondary" onClick={() => handleOpenPreview(String(item.id), item.name)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={() => handleEdit(item)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(item)}><DeleteIcon fontSize="small" /></IconButton>
                  </Paper>
                ))}
              </Box>
            )}
          </>
        )}

        {editing && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              label={tt('Şablon Adı', 'Template Name')}
              value={editing.name}
              onChange={(e) => setEditing(prev => prev && { ...prev, name: e.target.value })}
              inputProps={{ maxLength: 200 }}
              required
            />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                select
                size="small"
                label={tt('Belge Türü', 'Document Type')}
                value={editing.documentType}
                onChange={(e) => setEditing(prev => prev && { ...prev, documentType: e.target.value })}
                sx={{ minWidth: 180 }}
                helperText={tt('Şablonun geçerli olduğu belgeler', 'Documents this template applies to')}
              >
                <MenuItem value="HEPSI">{tt('Hepsi (e-Fatura + e-Arşiv)', 'All (e-Invoice + e-Archive)')}</MenuItem>
                <MenuItem value="EFATURA">{tt('Sadece E-Fatura', 'E-Invoice only')}</MenuItem>
                <MenuItem value="EARSIV">{tt('Sadece E-Arşiv', 'E-Archive only')}</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label={tt('Kapsam', 'Scope')}
                value={editing.branchId}
                onChange={(e) => setEditing(prev => prev && { ...prev, branchId: e.target.value })}
                sx={{ minWidth: 220 }}
                helperText={tt('Şubeye özel şablon şirket genelini ezer', 'Branch-specific template overrides company-wide')}
              >
                <MenuItem value="">{tt('Şirket Geneli', 'Company-wide')}</MenuItem>
                {(branches || []).filter((b: any) => b.id !== 'null').map((b: any) => (
                  <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                ))}
              </TextField>
              <FormControlLabel
                control={
                  <Switch
                    checked={editing.isDefault}
                    onChange={(e) => setEditing(prev => prev && { ...prev, isDefault: e.target.checked })}
                    color="warning"
                  />
                }
                label={tt('Varsayılan şablon', 'Default template')}
              />
            </Box>

            <Divider />

            {/* Tasarıma özel görseller: aynı VKN altında farklı markalar için şablon başına
                logo/kaşe. Boş bırakılırsa şube → şirket görseline düşülür. */}
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'rgba(156, 39, 176, 0.04)' }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                {tt('Bu Tasarıma Özel Görseller', 'Images Specific to This Design')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {tt(
                  'Bu şablonla kesilen faturalarda bu görseller kullanılır (ör. aynı vergi numarası altında farklı markalar). Boş bırakılırsa şube/şirket görseli kullanılır.',
                  'Invoices sent with this template use these images (e.g. different brands under the same tax number). If left empty, the branch/company image is used.'
                )}
              </Typography>

              {(['logo', 'stamp'] as const).map((slot) => {
                const current = slot === 'logo' ? editing.logoUrl : editing.stampUrl;
                const label = slot === 'logo' ? tt('Logo', 'Logo') : tt('Kaşe / İmza', 'Stamp / Signature');
                const fileRef = slot === 'logo' ? tplLogoFileInputRef : tplStampFileInputRef;
                return (
                  <Box key={slot} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: slot === 'logo' ? 1.5 : 0 }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 90 }}>{label}</Typography>
                    <Box
                      sx={{
                        width: 140,
                        height: 70,
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'white',
                        overflow: 'hidden',
                      }}
                    >
                      {current ? (
                        <Box component="img" src={current} alt={label} sx={{ maxWidth: '100%', maxHeight: '100%' }} />
                      ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', px: 0.5 }}>
                          {tt('Şube/şirket görseli kullanılır', 'Branch/company image is used')}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PhotoLibraryIcon />}
                        onClick={() => setLibrarySlot({ slot, target: 'template' })}
                      >
                        {tt('Kütüphaneden Seç', 'Select from Library')}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<UploadFileIcon />}
                        onClick={() => fileRef.current?.click()}
                      >
                        {tt('Bilgisayardan Yükle', 'Upload from Computer')}
                      </Button>
                      {current && (
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={<DeleteIcon />}
                          onClick={() => setTemplateImage(slot, '')}
                        >
                          {tt('Kaldır', 'Remove')}
                        </Button>
                      )}
                      <input
                        ref={fileRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        hidden
                        onChange={(e) => handleTemplateImageFile(slot, e)}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Paper>

            <Alert severity="info" sx={{ py: 0.5 }}>
              {tt('İçeriği düzenlemeniz gerekmez; isim verip kaydedin.', 'You do not need to edit the content; just name it and save.')}
            </Alert>

            <TextField
              fullWidth
              multiline
              minRows={10}
              maxRows={18}
              label={tt('XSLT İçeriği', 'XSLT Content')}
              value={editing.content}
              onChange={(e) => setEditing(prev => prev && { ...prev, content: e.target.value })}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
              helperText={tt(
                `${(editing.content.length / 1024).toFixed(0)} KB — logo alanı: __FIRMA_LOGO__ (gönderimde otomatik doldurulur)`,
                `${(editing.content.length / 1024).toFixed(0)} KB — logo placeholder: __FIRMA_LOGO__ (filled automatically on send)`
              )}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {editing ? (
          <>
            <Button onClick={() => setEditing(null)} disabled={saving}>{tt('Geri', 'Back')}</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {saving ? tt('Kaydediliyor...', 'Saving...') : tt('Kaydet', 'Save')}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>{tt('Kapat', 'Close')}</Button>
        )}
      </DialogActions>

      {/* Logo/Kaşe: görsel kütüphanesinden tekli seçim (seçilen URL gönderimde base64'e çevrilir).
          target 'scope' → şirket/şube kaydına yazılır; 'template' → düzenlenen şablona yazılır. */}
      <ImageLibrarySelectionModal
        open={librarySlot !== null}
        onClose={() => setLibrarySlot(null)}
        onSave={(urls: string[]) => {
          const sel = librarySlot;
          setLibrarySlot(null);
          if (!sel || !urls || urls.length === 0) return;
          if (sel.target === 'template') setTemplateImage(sel.slot, urls[0]);
          else saveImage(sel.slot, urls[0]);
        }}
        companyId={companyId || null}
        productName={librarySlot?.slot === 'stamp' ? tt('Kaşe / İmza', 'Stamp / Signature') : tt('Fatura Logosu', 'Invoice Logo')}
        productId={null}
        currentImageUrls={(() => {
          if (!librarySlot) return [];
          const cur = librarySlot.target === 'template'
            ? (librarySlot.slot === 'logo' ? editing?.logoUrl : editing?.stampUrl) || null
            : currentImages[librarySlot.slot];
          return cur && !cur.startsWith('data:') ? [cur] : [];
        })()}
        singleSelect
        maxSelection={1}
        noticeMessage={librarySlot?.slot === 'stamp'
          ? tt(
              'Seçtiğiniz görsel, e-fatura tasarımının alt kısmındaki kaşe/imza alanında kullanılır.',
              'The selected image is used in the stamp/signature area at the bottom of the e-invoice design.'
            )
          : tt(
              'Seçtiğiniz görsel, e-fatura tasarımındaki logo alanında kullanılır.',
              'The selected image is used in the logo area of the e-invoice design.'
            )}
      />

      {/* Görsel Önizleme: şablon, seçilen gerçek faturayla HTML'e dönüştürülüp gösterilir */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VisibilityIcon color="secondary" />
          <Box sx={{ flex: 1 }}>
            {tt('Şablon Önizleme', 'Template Preview')}{previewTemplateName ? ` — ${previewTemplateName}` : ''}
          </Box>
          <IconButton onClick={() => setPreviewOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1.5 }}>
          <TextField
            select
            size="small"
            fullWidth
            label={tt('Örnek Fatura', 'Sample Invoice')}
            value={previewInvoiceId}
            onChange={(e) => {
              setPreviewInvoiceId(e.target.value);
              if (e.target.value) runPreview(previewTemplateSel, e.target.value);
            }}
            sx={{ mb: 1.5 }}
            helperText={tt(
              'Tasarımın nasıl görüneceğini göstermek için kayıtlı bir faturanız kullanılır (gönderim yapılmaz).',
              'One of your saved invoices is used to render the design (nothing is sent).'
            )}
          >
            {sampleInvoices.length === 0 && (
              <MenuItem value="" disabled>{tt('Fatura bulunamadı', 'No invoice found')}</MenuItem>
            )}
            {sampleInvoices.map((inv: any) => (
              <MenuItem key={inv.id} value={inv.id}>
                {inv.invoiceNumber || inv.id} — {inv.accountName || ''}
                {inv.invoiceDate ? ` (${new Date(inv.invoiceDate).toLocaleDateString(isTr ? 'tr-TR' : 'en-US')})` : ''}
              </MenuItem>
            ))}
          </TextField>

          {previewLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6, gap: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2">{tt('Önizleme oluşturuluyor...', 'Generating preview...')}</Typography>
            </Box>
          )}

          {previewError && <Alert severity="warning">{previewError}</Alert>}

          {!previewLoading && previewHtml && (
            <Box
              component="iframe"
              srcDoc={previewHtml}
              title={tt('Şablon önizleme', 'Template preview')}
              // allow-scripts: şablona gömülü karekod üretici çalışsın (allow-same-origin
              // verilmediği için iframe izole kalır, üst pencereye erişemez).
              sandbox="allow-scripts"
              sx={{ width: '100%', height: '65vh', border: '1px solid', borderColor: 'divider', borderRadius: 1, backgroundColor: 'white' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>{tt('Kapat', 'Close')}</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default XsltTemplateModal;
